import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dropdown,
  DropdownMenuItem,
  Paper,
  Stack,
  SwitchCheckbox,
  TextField,
  Typography,
} from 'views/components';
import {SendOutlined} from 'views/icons';
import {Translate, translate} from 'views/module';

import ConfirmationDialog from './ConfirmationDialog';
import ProfileCategoryFilters from './ProfileCategoryFilters';
import RecipientTable from './RecipientTable';
import {CHANNELS, ENTITY_TYPES, createRecipient, matchesProfileCategoryFilters} from './utils';

import backend from 'utils/backend';

const PAGE_SEND_DELAY_MS = 5000;
const INITIAL_SEND_DELAY_MS = PAGE_SEND_DELAY_MS;
const DRY_RUN_REQUEST_DELAY_MS = 250;
const DRY_RUN_AVAILABLE = process.env.NODE_ENV !== 'production';

const SEND_PHASES = {
  INITIAL_DELAY: 'INITIAL_DELAY',
  SENDING: 'SENDING',
  BETWEEN_PAGES: 'BETWEEN_PAGES',
};

const DELIVERY_FAILURE_CODES = {
  NO_USER_LINK_FOUND: 'NO_USER_LINK_FOUND',
  NO_USER_EMAIL: 'NO_USER_EMAIL',
  SMS_SEND_NOT_CONFIRMED: 'SMS_SEND_NOT_CONFIRMED',
  EMAIL_SEND_NOT_CONFIRMED: 'EMAIL_SEND_NOT_CONFIRMED',
  SUPPORT_MESSAGE_NOT_CREATED: 'SUPPORT_MESSAGE_NOT_CREATED',
  REQUEST_FAILED: 'REQUEST_FAILED',
};

const ENTITY_OPTIONS = [
  ENTITY_TYPES.CAREGIVER,
  ENTITY_TYPES.CLIENT,
];

const CHANNEL_OPTIONS = {
  [ENTITY_TYPES.CAREGIVER]: [
    CHANNELS.SMS,
    CHANNELS.EMAIL,
    CHANNELS.SUPPORT_CHAT,
  ],
  [ENTITY_TYPES.CLIENT]: [
    CHANNELS.SMS,
    CHANNELS.EMAIL,
  ],
};

const wait = (milliseconds) => {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
};

const createDeliveryError = (failureCode) => {
  const error = new Error(failureCode);
  error.deliveryFailureCode = failureCode;
  return error;
};

const getDeliveryFailureCode = (error) => {
  const failureCode = error?.deliveryFailureCode;
  if (Object.values(DELIVERY_FAILURE_CODES).includes(failureCode)) {
    return failureCode;
  }

  return DELIVERY_FAILURE_CODES.REQUEST_FAILED;
};

const BulkMessaging = (props) => {
  const {user} = props;
  const [entityType, setEntityType] = React.useState(ENTITY_TYPES.CAREGIVER);
  const [selectedStatus, setSelectedStatus] = React.useState('');
  const [missingCategories, setMissingCategories] = React.useState([]);
  const [channel, setChannel] = React.useState('');
  const [subject, setSubject] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [dryRun, setDryRun] = React.useState(false);
  const [queryPages, setQueryPages] = React.useState([]);
  const [queryLoading, setQueryLoading] = React.useState(false);
  const [queryFailed, setQueryFailed] = React.useState(false);
  const [supportConversations, setSupportConversations] = React.useState({});
  const [supportLoading, setSupportLoading] = React.useState(false);
  const [deliveryById, setDeliveryById] = React.useState({});
  const [confirmationOpen, setConfirmationOpen] = React.useState(false);
  const [isSending, setIsSending] = React.useState(false);
  const [cancelRequested, setCancelRequested] = React.useState(false);
  const [sendPhase, setSendPhase] = React.useState(null);
  const [secondsUntilNextPage, setSecondsUntilNextPage] = React.useState(0);
  const [sendSummary, setSendSummary] = React.useState(null);
  const queryRequestIdRef = React.useRef(0);
  const supportRequestIdRef = React.useRef(0);
  const cancelRequestedRef = React.useRef(false);

  const statusDomain = entityType === ENTITY_TYPES.CAREGIVER ? 'caregiver' : 'client';
  const statusOptions = React.useMemo(() => {
    return Object.entries(translate(`${statusDomain}.Status`)).map(([key, value]) => ({
      key,
      label: value.Title,
    }));
  }, [statusDomain]);
  const channelOptions = CHANNEL_OPTIONS[entityType];
  const totalResults = queryPages.reduce((count, page) => count + page.length, 0);

  React.useEffect(() => {
    const requestId = queryRequestIdRef.current + 1;
    queryRequestIdRef.current = requestId;
    setQueryPages([]);
    setQueryFailed(false);
    setDeliveryById({});
    setSendSummary(null);

    if (!selectedStatus) {
      setQueryLoading(false);
      return;
    }

    const loadAllPages = async () => {
      setQueryLoading(true);
      const loadedPages = [];
      let lastEvaluatedKey = null;

      try {
        do {
          const filters = [{key: 'status', value: selectedStatus}];
          const sort = {sortKey: 'createdAt', sortDirection: 'DESC'};
          const result = entityType === ENTITY_TYPES.CAREGIVER ?
            await backend.caregiver.queryCaregiver(filters, sort, lastEvaluatedKey) :
            await backend.client.queryClient(filters, sort, lastEvaluatedKey);

          if (requestId !== queryRequestIdRef.current) {
            return;
          }

          const nextPage = result?.list || [];
          if (nextPage.length > 0) {
            loadedPages.push(nextPage);
            setQueryPages([...loadedPages]);
          }
          lastEvaluatedKey = result?.lastEvaluatedKey || null;
        } while (lastEvaluatedKey);
      } catch (error) {
        if (requestId !== queryRequestIdRef.current) {
          return;
        }

        console.error(error);
        setQueryPages([]);
        setQueryFailed(true);
      } finally {
        if (requestId === queryRequestIdRef.current) {
          setQueryLoading(false);
        }
      }
    };

    loadAllPages();
  }, [entityType, selectedStatus]);

  const hasProfileCategoryFilters = missingCategories.length > 0;
  const audiencePages = React.useMemo(() => {
    if (entityType !== ENTITY_TYPES.CAREGIVER) {
      return queryPages;
    }

    if (!hasProfileCategoryFilters) {
      return [];
    }

    return queryPages.map((page) => page.filter((caregiver) => {
      return matchesProfileCategoryFilters(caregiver, missingCategories);
    }));
  }, [entityType, hasProfileCategoryFilters, missingCategories, queryPages]);
  const audienceCount = audiencePages.reduce((count, page) => count + page.length, 0);

  React.useEffect(() => {
    const requestId = supportRequestIdRef.current + 1;
    supportRequestIdRef.current = requestId;
    setSupportConversations({});

    if (
      entityType !== ENTITY_TYPES.CAREGIVER ||
      channel !== CHANNELS.SUPPORT_CHAT ||
      queryLoading ||
      audienceCount === 0
    ) {
      setSupportLoading(false);
      return;
    }

    const loadSupportConversations = async () => {
      setSupportLoading(true);
      const conversations = {};

      for (const page of audiencePages) {
        const pageResults = await Promise.all(page.map(async (caregiver) => {
          try {
            const result = await backend.communication.getSupportConversation(caregiver.caregiverRn);
            return [caregiver.caregiverRn, result?.conversationRn || null];
          } catch (error) {
            console.error(error);
            return [caregiver.caregiverRn, null];
          }
        }));

        if (requestId !== supportRequestIdRef.current) {
          return;
        }

        Object.assign(conversations, Object.fromEntries(pageResults));
        setSupportConversations({...conversations});
      }

      if (requestId === supportRequestIdRef.current) {
        setSupportLoading(false);
      }
    };

    loadSupportConversations();
  }, [audienceCount, audiencePages, channel, entityType, queryLoading]);

  const recipientPages = React.useMemo(() => {
    return audiencePages.map((page) => page.map((item) => {
      return createRecipient(item, entityType, channel, supportConversations);
    }));
  }, [audiencePages, channel, entityType, supportConversations]);
  const recipients = recipientPages.reduce((output, page) => [...output, ...page], []);
  const eligibleCount = recipients.filter((recipient) => recipient.eligible).length;
  const skippedCount = channel ? recipients.length - eligibleCount : 0;
  const selectedStatusLabel = statusOptions.find((option) => option.key === selectedStatus)?.label || '';
  const entityTypeLabel = translate(`bulkMessaging.EntityTypes.${entityType}`);
  const channelLabel = channel ? translate(`bulkMessaging.Channels.${channel}`) : '';
  const missingCategoriesLabel = missingCategories.map((category) => {
    return translate(`bulkMessaging.ProfileCategories.Options.${category}`);
  }).join(', ');
  const senderName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
  const requiresSenderEmail = channel === CHANNELS.SMS || channel === CHANNELS.EMAIL;
  const hasRequiredAudienceFilters = entityType !== ENTITY_TYPES.CAREGIVER || hasProfileCategoryFilters;
  const canSend = Boolean(
      selectedStatus &&
      hasRequiredAudienceFilters &&
      channel &&
      message.trim() &&
      eligibleCount > 0 &&
      !queryLoading &&
      !queryFailed &&
      !supportLoading &&
      !isSending &&
      (!requiresSenderEmail || user?.email) &&
      (channel !== CHANNELS.EMAIL || subject.trim()),
  );

  const onEntityTypeChange = (nextEntityType) => {
    setEntityType(nextEntityType);
    setSelectedStatus('');
    setMissingCategories([]);
    setChannel('');
    setConfirmationOpen(false);
  };

  const onStatusChange = (nextStatus) => {
    setSelectedStatus(nextStatus);
    setConfirmationOpen(false);
  };

  const onChannelChange = (nextChannel) => {
    setChannel(nextChannel);
    setDeliveryById({});
    setSendSummary(null);
    setConfirmationOpen(false);
  };

  const onDryRunChange = (nextDryRun) => {
    setDryRun(nextDryRun);
    setDeliveryById({});
    setSendSummary(null);
    setConfirmationOpen(false);
  };

  const resetCategoryFilterDeliveryState = () => {
    setDeliveryById({});
    setSendSummary(null);
    setConfirmationOpen(false);
  };

  const onMissingCategoriesChange = (nextMissingCategories) => {
    setMissingCategories(nextMissingCategories);
    resetCategoryFilterDeliveryState();
  };

  const clearProfileCategoryFilters = () => {
    setMissingCategories([]);
    resetCategoryFilterDeliveryState();
  };

  const waitForSendBoundary = async (milliseconds, phase) => {
    const endTime = Date.now() + milliseconds;
    setSendPhase(phase);
    setSecondsUntilNextPage(Math.ceil(milliseconds / 1000));

    while (Date.now() < endTime) {
      if (cancelRequestedRef.current) {
        return false;
      }

      const millisecondsRemaining = endTime - Date.now();
      setSecondsUntilNextPage(Math.max(1, Math.ceil(millisecondsRemaining / 1000)));
      await wait(Math.min(250, millisecondsRemaining));
    }

    setSecondsUntilNextPage(0);
    return !cancelRequestedRef.current;
  };

  const cancelSend = () => {
    cancelRequestedRef.current = true;
    setCancelRequested(true);
  };

  const sendToRecipient = async (recipient) => {
    if (dryRun) {
      await wait(DRY_RUN_REQUEST_DELAY_MS);
      return;
    }

    if (channel === CHANNELS.SMS) {
      const response = await backend.notification.sendSms(recipient.targetRn, message.trim(), user.email);
      const status = response?.result?.smsResult?.status;
      if (status !== 'SUCCESS') {
        const failureCode = status === DELIVERY_FAILURE_CODES.NO_USER_LINK_FOUND ?
          DELIVERY_FAILURE_CODES.NO_USER_LINK_FOUND :
          DELIVERY_FAILURE_CODES.SMS_SEND_NOT_CONFIRMED;
        throw createDeliveryError(failureCode);
      }

      return;
    }

    if (channel === CHANNELS.EMAIL) {
      const response = await backend.notification.sendEmail(recipient.targetRn, {
        subject: subject.trim(),
        message: message.trim(),
        fromName: senderName,
        fromEmail: user.email,
      });
      const status = response?.result?.emailResult?.status;
      if (status !== 'SUCCESS') {
        const failureCode = status === DELIVERY_FAILURE_CODES.NO_USER_EMAIL ?
          DELIVERY_FAILURE_CODES.NO_USER_EMAIL :
          DELIVERY_FAILURE_CODES.EMAIL_SEND_NOT_CONFIRMED;
        throw createDeliveryError(failureCode);
      }

      return;
    }

    const response = await backend.communication.createConversationMessage(
        recipient.resourceRn,
        recipient.conversationRn,
        {
          message: message.trim(),
          requesterRn: user.userRn,
          source: 'AGENT',
        },
    );
    if (!response?.message?.messageRn) {
      throw createDeliveryError(DELIVERY_FAILURE_CODES.SUPPORT_MESSAGE_NOT_CREATED);
    }
  };

  const sendAllPages = async () => {
    setConfirmationOpen(false);
    setIsSending(true);
    cancelRequestedRef.current = false;
    setCancelRequested(false);
    setSendSummary(null);

    const initialDelivery = {};
    recipients.forEach((recipient) => {
      if (!recipient.eligible) {
        initialDelivery[recipient.id] = {status: 'SKIPPED'};
      }
    });
    setDeliveryById(initialDelivery);

    let sentCount = 0;
    let failedCount = 0;
    let wasCanceled = false;
    const eligiblePages = recipientPages
        .map((page) => page.filter((recipient) => recipient.eligible))
        .filter((page) => page.length > 0);

    try {
      const shouldStart = await waitForSendBoundary(INITIAL_SEND_DELAY_MS, SEND_PHASES.INITIAL_DELAY);
      if (!shouldStart) {
        wasCanceled = true;
        return;
      }

      for (let pageIndex = 0; pageIndex < eligiblePages.length; pageIndex += 1) {
        const page = eligiblePages[pageIndex];
        setSendPhase(SEND_PHASES.SENDING);
        setDeliveryById((currentDelivery) => {
          const nextDelivery = {...currentDelivery};
          page.forEach((recipient) => {
            nextDelivery[recipient.id] = {status: dryRun ? 'SIMULATING' : 'SENDING'};
          });
          return nextDelivery;
        });

        const results = await Promise.allSettled(page.map(sendToRecipient));
        results.forEach((result) => {
          if (result.status === 'fulfilled') {
            sentCount += 1;
          } else {
            failedCount += 1;
          }
        });
        setDeliveryById((currentDelivery) => {
          const nextDelivery = {...currentDelivery};
          results.forEach((result, resultIndex) => {
            const recipient = page[resultIndex];
            const status = result.status === 'fulfilled' ?
              dryRun ? 'WOULD_SEND' : 'SENT' :
              'FAILED';
            nextDelivery[recipient.id] = {
              status,
              ...(result.status === 'rejected' ? {
                failureCode: getDeliveryFailureCode(result.reason),
              } : {}),
            };
          });
          return nextDelivery;
        });

        if (cancelRequestedRef.current) {
          wasCanceled = true;
          break;
        }

        if (pageIndex < eligiblePages.length - 1) {
          const shouldContinue = await waitForSendBoundary(
              PAGE_SEND_DELAY_MS,
              SEND_PHASES.BETWEEN_PAGES,
          );
          if (!shouldContinue) {
            wasCanceled = true;
            break;
          }
        }
      }
    } finally {
      const canceledCount = wasCanceled ? eligibleCount - sentCount - failedCount : 0;
      if (wasCanceled) {
        setDeliveryById((currentDelivery) => {
          const nextDelivery = {...currentDelivery};
          recipients.forEach((recipient) => {
            if (recipient.eligible && !nextDelivery[recipient.id]) {
              nextDelivery[recipient.id] = {status: 'CANCELED'};
            }
          });
          return nextDelivery;
        });
      }
      setSendSummary({
        sent: sentCount,
        failed: failedCount,
        skipped: skippedCount,
        canceled: canceledCount,
        wasCanceled,
      });
      setSendPhase(null);
      setSecondsUntilNextPage(0);
      setIsSending(false);
    }
  };

  let sendingStatusExpression = 'bulkMessaging.Sending.ActivePage';
  if (cancelRequested) {
    sendingStatusExpression = 'bulkMessaging.Sending.CancelRequested';
  } else if (sendPhase === SEND_PHASES.INITIAL_DELAY) {
    sendingStatusExpression = 'bulkMessaging.Sending.InitialDelay';
  } else if (sendPhase === SEND_PHASES.BETWEEN_PAGES) {
    sendingStatusExpression = 'bulkMessaging.Sending.BetweenPages';
  }

  let summaryExpression = dryRun ? 'bulkMessaging.Summary.DryComplete' : 'bulkMessaging.Summary.Complete';
  if (sendSummary?.wasCanceled) {
    summaryExpression = dryRun ? 'bulkMessaging.Summary.DryCanceled' : 'bulkMessaging.Summary.Canceled';
  }

  return (
    <Stack spacing={3} sx={{width: '100%', p: {xs: 1, md: 3}}}>
      <Box>
        <Typography variant='h4' sx={{fontWeight: 700}}>
          <Translate expression='bulkMessaging.Title' />
        </Typography>
        <Typography color='text.secondary' sx={{mt: 0.5}}>
          <Translate expression='bulkMessaging.Subtitle' />
        </Typography>
      </Box>

      {
        DRY_RUN_AVAILABLE &&
        <Alert severity={dryRun ? 'warning' : 'info'}>
          <Stack
            direction={{xs: 'column', sm: 'row'}}
            spacing={1}
            alignItems={{xs: 'flex-start', sm: 'center'}}
            justifyContent='space-between'
          >
            <Box>
              <Typography sx={{fontWeight: 700}}>
                <Translate expression={dryRun ? 'bulkMessaging.DryRun.Active' : 'bulkMessaging.DryRun.Title'} />
              </Typography>
              <Typography sx={{fontSize: '13px'}}>
                <Translate expression='bulkMessaging.DryRun.Description' />
              </Typography>
            </Box>
            <SwitchCheckbox
              disabled={isSending}
              onChange={onDryRunChange}
              value={dryRun}
            />
          </Stack>
        </Alert>
      }

      <Paper sx={{p: {xs: 2, md: 3}}}>
        <Stack spacing={2.5}>
          <Stack direction={{xs: 'column', md: 'row'}} spacing={2}>
            <Dropdown
              disabled={isSending}
              label={translate('bulkMessaging.Fields.EntityType')}
              onChange={onEntityTypeChange}
              value={entityType}
            >
              {
                ENTITY_OPTIONS.map((entityOption) => (
                  <DropdownMenuItem key={entityOption} value={entityOption}>
                    {translate(`bulkMessaging.EntityTypes.${entityOption}`)}
                  </DropdownMenuItem>
                ))
              }
            </Dropdown>
            <Dropdown
              disabled={isSending}
              label={translate('bulkMessaging.Fields.Status')}
              onChange={onStatusChange}
              value={selectedStatus}
            >
              {
                statusOptions.map((statusOption) => (
                  <DropdownMenuItem key={statusOption.key} value={statusOption.key}>
                    {statusOption.label}
                  </DropdownMenuItem>
                ))
              }
            </Dropdown>
            <Dropdown
              disabled={isSending}
              label={translate('bulkMessaging.Fields.Channel')}
              onChange={onChannelChange}
              value={channel}
            >
              {
                channelOptions.map((channelOption) => (
                  <DropdownMenuItem key={channelOption} value={channelOption}>
                    {translate(`bulkMessaging.Channels.${channelOption}`)}
                  </DropdownMenuItem>
                ))
              }
            </Dropdown>
          </Stack>

          {
            requiresSenderEmail &&
            <Alert severity={user?.email ? 'info' : 'warning'}>
              <Translate
                expression={user?.email ?
                  'bulkMessaging.SenderEmail.Available' :
                  'bulkMessaging.SenderEmail.Missing'}
                substitute={{email: user?.email}}
              />
            </Alert>
          }

          {
            entityType === ENTITY_TYPES.CAREGIVER &&
            <ProfileCategoryFilters
              disabled={isSending}
              matchedCount={audienceCount}
              missingCategories={missingCategories}
              onClear={clearProfileCategoryFilters}
              onMissingCategoriesChange={onMissingCategoriesChange}
              totalCount={totalResults}
            />
          }

          {
            channel === CHANNELS.EMAIL &&
            <TextField
              disabled={isSending}
              fullWidth
              label={translate('bulkMessaging.Fields.Subject')}
              onChange={setSubject}
              required
              value={subject}
            />
          }
          <TextField
            disabled={isSending}
            fullWidth
            label={translate('bulkMessaging.Fields.Message')}
            minRows={6}
            multiline
            onChange={setMessage}
            value={message}
          />

          <Button
            color='error'
            disabled={!canSend}
            fullWidth
            loading={isSending}
            onClick={() => setConfirmationOpen(true)}
            startIcon={<SendOutlined />}
            sx={{minHeight: 64, fontSize: '18px', fontWeight: 700}}
          >
            <Translate expression={dryRun ? 'bulkMessaging.Actions.RunDry' : 'bulkMessaging.Actions.Send'} />
          </Button>
          {
            isSending &&
            <Alert severity={cancelRequested ? 'warning' : 'error'}>
              <Stack
                direction={{xs: 'column', sm: 'row'}}
                spacing={1}
                alignItems={{xs: 'flex-start', sm: 'center'}}
                justifyContent='space-between'
              >
                <Translate
                  expression={sendingStatusExpression}
                  substitute={{seconds: secondsUntilNextPage}}
                />
                <Button
                  color='error'
                  disabled={cancelRequested}
                  onClick={cancelSend}
                  variant='outlined'
                >
                  <Translate expression='bulkMessaging.Actions.Stop' />
                </Button>
              </Stack>
            </Alert>
          }
        </Stack>
      </Paper>

      {
        !selectedStatus &&
        <Alert severity='info'>
          <Translate expression='bulkMessaging.Query.SelectStatus' />
        </Alert>
      }
      {
        queryLoading &&
        <Alert severity='info' icon={<CircularProgress size={18} />}>
          <Translate expression='bulkMessaging.Query.Loading' />
          {` (${totalResults})`}
        </Alert>
      }
      {
        supportLoading &&
        <Alert severity='info' icon={<CircularProgress size={18} />}>
          <Translate expression='bulkMessaging.Query.Preflighting' />
        </Alert>
      }
      {
        queryFailed &&
        <Alert severity='error'>
          <Translate expression='bulkMessaging.Query.Failed' />
        </Alert>
      }
      {
        selectedStatus && !queryLoading && !queryFailed && totalResults === 0 &&
        <Alert severity='info'>
          <Translate expression='bulkMessaging.Query.Empty' />
        </Alert>
      }
      {
        selectedStatus && !queryLoading && !queryFailed && totalResults > 0 &&
        <Alert severity='success'>
          <Translate expression='bulkMessaging.Query.Loaded' substitute={{count: totalResults}} />
        </Alert>
      }
      {
        sendSummary &&
        <Alert severity={sendSummary.failed > 0 ? 'warning' : 'success'}>
          <Translate
            expression={summaryExpression}
            substitute={sendSummary}
          />
        </Alert>
      }
      {
        audienceCount > 0 &&
        <RecipientTable
          channel={channel}
          deliveryById={deliveryById}
          entityType={entityType}
          recipientPages={recipientPages}
        />
      }

      <ConfirmationDialog
        channelLabel={channelLabel}
        dryRun={dryRun}
        eligibleCount={eligibleCount}
        entityTypeLabel={entityTypeLabel}
        message={message.trim()}
        missingCategoriesLabel={missingCategoriesLabel}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={sendAllPages}
        open={confirmationOpen}
        senderEmail={requiresSenderEmail ? user?.email || '' : ''}
        skippedCount={skippedCount}
        statusLabel={selectedStatusLabel}
        subject={channel === CHANNELS.EMAIL ? subject.trim() : ''}
      />
    </Stack>
  );
};

BulkMessaging.propTypes = {
  user: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps, dispatch)(React.memo(BulkMessaging));
